import React, { useCallback, useEffect, useRef, useState } from 'react'
import Loader from 'Event/components/loader'
import { useExpansionPanelSummaryStyle } from 'Event/hooks/useExpansionPanelSummaryStyle'
import { EventExpansionPanelSummary } from 'Event/components/EventExpansionPanelSummary'
import { EventExpansionPanel } from 'Event/components/EventExpansionPanel'
import { EventExpansionPanelDetails } from 'Event/components/EventExpansionPanelDetails'
import useGetIntegrationKey, { emptyValue } from 'integrations/hooks/useGetIntegrationKey'
import { DonorPerfectConfig, IntegrationKey, IntegrationProvider } from 'integrations/types'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-material-ui'
import { SwitchToggle } from 'civic-champs-shared/formik/components'
import { makeStyles } from '@material-ui/styles'
import { ContainedButton, OutlinedButton } from 'civic-champs-shared'
import yup from 'civic-champs-shared/utils/yup'
import useDeleteIntegrationKeyDialog from 'integrations/hooks/useDeleteIntegrationKeyDialog'
import useSaveIntegrationKey from 'integrations/hooks/useSaveIntegrationKey'
import { Typography } from '@material-ui/core'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
    flexWrap: 'wrap',
  },
  input: {
    width: '800px',
    maxWidth: '100%',
  },
  section: {
    marginTop: '24px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '8px',
    color: '#333',
  },
  helperText: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '16px',
  },
})

const validationSchema = yup.object({
  token: yup.string().required('API Key is required'),
  config: yup.object({
    activityIdUdfName: yup.string().required('Activity ID UDF Name is required'),
    personIdUdfName: yup.string().required('Person ID UDF Name is required'),
    initialSyncDays: yup.number().positive('Must be a positive number').integer('Must be an integer'),
  }),
})

export function DonorPerfect({ scrollTop }: { scrollTop: number }) {
  const [expanded, setExpanded] = useState(true)
  const [loading, setLoading] = useState(true)
  const [integrationKey, setIntegrationKey] = useState<IntegrationKey>({
    ...emptyValue,
    config: {
      activityIdUdfName: 'CC_ActivityID',
      personIdUdfName: 'CC_PersonID',
      initialSyncDays: 30,
    },
  })
  const summaryRef = useRef<HTMLDivElement>(null)
  const detailsRef = useRef<HTMLDivElement>(null)
  const style = useExpansionPanelSummaryStyle({
    expanded,
    scrollTop,
    summaryRef,
    detailsRef,
  })
  const classes = useStyles()

  const fetchIntegrationKey = useGetIntegrationKey()
  const refresh = useCallback(async () => {
    setLoading(true)
    const key = await fetchIntegrationKey(IntegrationProvider.DONOR_PERFECT)
    const hasConfig = key.config && (
      key.config.activityIdUdfName ||
      key.config.personIdUdfName
    )
    setIntegrationKey({
      ...key,
      config: hasConfig ? {
        activityIdUdfName: 'CC_ActivityID',
        personIdUdfName: 'CC_PersonID',
        initialSyncDays: 30,
        ...key.config,
      } : {
        activityIdUdfName: 'CC_ActivityID',
        personIdUdfName: 'CC_PersonID',
        initialSyncDays: 30,
      },
    })
    setLoading(false)
  }, [fetchIntegrationKey])

  useEffect(() => {
    refresh()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const saveIntegrationKey = useSaveIntegrationKey<DonorPerfectConfig>()
  const onSubmit = useCallback(
    async (values: IntegrationKey) => {
      await saveIntegrationKey({
        token: values.token,
        enabled: values.enabled,
        provider: IntegrationProvider.DONOR_PERFECT,
        config: values.config,
      })
      await refresh()
    },
    [refresh, saveIntegrationKey],
  )

  const showDeleteDialog = useDeleteIntegrationKeyDialog({ onSuccess: refresh })
  const onDelete = useCallback(() => {
    showDeleteDialog(IntegrationProvider.DONOR_PERFECT)
  }, [showDeleteDialog])

  if (loading) {
    return <Loader />
  }

  return (
    <EventExpansionPanel onChange={(_, expanded) => setExpanded(expanded)}>
      <EventExpansionPanelSummary style={style} ref={summaryRef} name="donor-perfect">
        <h1>DonorPerfect</h1>
      </EventExpansionPanelSummary>
      <EventExpansionPanelDetails ref={detailsRef}>
        <Formik
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          initialValues={integrationKey}
          enableReinitialize
        >
          {({ isSubmitting, submitForm }) => (
            <Form>
              <div className={classes.container}>
                <div className={classes.input}>
                  <Field
                    variant="outlined"
                    fullWidth
                    component={TextField}
                    name="token"
                    placeholder="DonorPerfect API Key"
                    label="API Key*"
                    helperText="Your DonorPerfect XML API Key"
                  />
                </div>
              </div>

              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>User-Defined Field (UDF) Configuration</Typography>
                <Typography className={classes.helperText}>
                  Specify the UDF field names in DonorPerfect where CivicChamps data will be stored.
                </Typography>

                <div className={classes.container}>
                  <div className={classes.input}>
                    <Field
                      variant="outlined"
                      fullWidth
                      component={TextField}
                      name="config.activityIdUdfName"
                      placeholder="CC_ActivityID"
                      label="Activity ID UDF Name*"
                      helperText="UDF field for storing CivicChamps activity ID (attached to OtherInfo record)"
                    />
                  </div>
                </div>

                <div className={classes.container}>
                  <div className={classes.input}>
                    <Field
                      variant="outlined"
                      fullWidth
                      component={TextField}
                      name="config.personIdUdfName"
                      placeholder="CC_PersonID"
                      label="Person ID UDF Name*"
                      helperText="UDF field for storing CivicChamps person ID (attached to Donor record)"
                    />
                  </div>
                </div>
              </div>

              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>Sync Configuration</Typography>
                <Typography className={classes.helperText}>
                  Configure how activities are synchronized during the first sync.
                </Typography>

                <div className={classes.container}>
                  <div className={classes.input}>
                    <Field
                      variant="outlined"
                      fullWidth
                      component={TextField}
                      name="config.initialSyncDays"
                      type="number"
                      placeholder="30"
                      label="Initial Sync Days"
                      helperText="Number of days to sync on first run (default: 30). After the first sync, only new activities will be synced daily."
                    />
                  </div>
                </div>
              </div>

              <div className={classes.container}>
                <Field component={SwitchToggle} name="enabled" label="Enabled" />
              </div>

              <div className={classes.container}>
                <OutlinedButton disabled={!integrationKey.id} onClick={onDelete}>
                  Delete
                </OutlinedButton>
                <ContainedButton disabled={isSubmitting} isLoading={isSubmitting} onClick={submitForm}>
                  Save
                </ContainedButton>
              </div>
            </Form>
          )}
        </Formik>
      </EventExpansionPanelDetails>
    </EventExpansionPanel>
  )
}
